import { Box, Button } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { formatMinutes } from '@/shared/api/timeTracking';
import { Icon } from '@/shared/components/Icon';
import { useHasPermission } from '@/shared/session/permissions';

import { currentTimerQueryOptions } from './timeOptions';

/**
 * The clock of the top bar (13 §3.1, pendiente 6): while one runs, every screen says so and one tap
 * goes back to its ticket, where it is stopped. It reads the same query the ticket and `/my-work`
 * poll, so a running clock costs one request a minute wherever the person is, and it asks nothing
 * of somebody who records no time. With no clock running it renders nothing at all.
 */
export function RunningTimerIndicator() {
  const { t } = useTranslation('time');
  const navigate = useNavigate();
  const canRecord = useHasPermission('time_entries.create');
  const timer = useQuery({ ...currentTimerQueryOptions(), enabled: canRecord });
  const running = timer.data ?? null;

  if (!canRecord || running === null) {
    return null;
  }

  const elapsed = formatMinutes(running.elapsedMinutes);
  return (
    <Button
      size="compact-sm"
      variant="light"
      color="teal"
      leftSection={<Icon name="clock" size={14} />}
      aria-label={t('timer.headerLabel', { ticket: running.ticketNumber, elapsed })}
      onClick={() =>
        void navigate({ to: '/tickets/$ticketId', params: { ticketId: running.ticketId } })
      }
    >
      {/* On a phone only the elapsed time: the ticket number is what takes the room the wordmark
          needs, and the whole label stays as the accessible name (ronda 4, punto 6). */}
      <Box component="span" visibleFrom="sm">
        {t('timer.header', { ticket: running.ticketNumber, elapsed })}
      </Box>
      <Box component="span" hiddenFrom="sm">
        {elapsed}
      </Box>
    </Button>
  );
}
